export type ProjectKind = "mesh" | "pointcloud";

export type Project = {
  id: string;
  name: string;
  clientLabel: string | null;
  kind: ProjectKind;
  crs: string | null;
  status: "uploading" | "processing" | "ready" | "failed";
  shareToken: string;
  shareEnabled: boolean;
  shareUrl: string;
  storagePrefix: string;
  byteSize: string;
  errorMessage: string | null;
  filesTotal: number;
  filesWritten: number;
  progress: number;
  tilesetUrl: string | null;
  createdAt: string;
  updatedAt: string;
};

export type UploadPlan =
  | {
      mode: "chunked";
      chunkSize: number;
      local: false;
      driver?: string;
    }
  | {
      mode: "direct";
      method: string;
      url: string;
      headers: Record<string, string>;
      local: boolean;
      driver?: string;
    }
  // Back-compat with older API responses
  | {
      mode?: undefined;
      method: string;
      url: string;
      headers: Record<string, string>;
      local: boolean;
      driver?: string;
    };

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    ...init,
    credentials: "include",
    headers: {
      ...(init?.body instanceof FormData || init?.body instanceof Blob
        ? {}
        : { "Content-Type": "application/json" }),
      ...init?.headers,
    },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(err.error || `Request failed (${res.status})`);
  }
  return res.json() as Promise<T>;
}

export const api = {
  me: () => request<{ authenticated: boolean; email?: string }>("/api/auth/me"),
  login: (email: string, password: string) =>
    request<{ email: string }>("/api/auth/login", {
      method: "POST",
      body: JSON.stringify({ email, password }),
    }),
  logout: () => request<{ ok: boolean }>("/api/auth/logout", { method: "POST" }),
  listProjects: () => request<Project[]>("/api/admin/projects"),
  getProject: (id: string) => request<Project>(`/api/admin/projects/${id}`),
  createProject: (data: {
    name: string;
    clientLabel?: string;
    byteSize?: number;
    kind: ProjectKind;
    format?: "3tz";
  }) =>
    request<{ project: Project; upload: UploadPlan }>("/api/admin/projects", {
      method: "POST",
      body: JSON.stringify({ ...data, format: "3tz" }),
    }),
  completeUpload: (id: string) =>
    request<{ ok: boolean }>(`/api/admin/projects/${id}/complete-upload`, { method: "POST" }),
  revokeShare: (id: string) =>
    request<Project>(`/api/admin/projects/${id}/revoke-share`, { method: "POST" }),
  enableShare: (id: string) =>
    request<Project>(`/api/admin/projects/${id}/enable-share`, { method: "POST" }),
  deleteProject: (id: string) =>
    request<{ ok: boolean }>(`/api/admin/projects/${id}`, { method: "DELETE" }),
  share: (token: string) =>
    request<{
      name: string;
      clientLabel: string | null;
      kind: ProjectKind;
      tilesetUrl: string;
    }>(`/api/share/${token}`),
};

export type UploadProgress = {
  loaded: number;
  total: number;
  percent: number;
  bytesPerSecond: number;
  etaSeconds: number | null;
  part: number | null;
  parts: number | null;
};

function trackUpload(total: number, parts: number | null) {
  const started = performance.now();
  return (loaded: number, part: number | null): UploadProgress => {
    const safeLoaded = Math.min(Math.max(loaded, 0), total);
    const elapsed = (performance.now() - started) / 1000;
    const bytesPerSecond = elapsed >= 0.4 && safeLoaded > 0 ? safeLoaded / elapsed : 0;
    const remaining = total - safeLoaded;
    const etaSeconds =
      bytesPerSecond > 0 && remaining > 0 ? remaining / bytesPerSecond : null;
    return {
      loaded: safeLoaded,
      total,
      percent: total > 0 ? Math.min(100, Math.round((safeLoaded / total) * 100)) : 0,
      bytesPerSecond,
      etaSeconds,
      part,
      parts,
    };
  };
}

function sendBody(
  method: string,
  url: string,
  body: Blob,
  headers: Record<string, string>,
  withCredentials: boolean,
  onLoaded: (loaded: number) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(method, url);
    Object.entries(headers).forEach(([key, value]) => xhr.setRequestHeader(key, value));
    xhr.withCredentials = withCredentials;
    xhr.upload.onprogress = (ev) => {
      if (ev.lengthComputable) onLoaded(ev.loaded);
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
        return;
      }
      let message = xhr.status === 413 ? "Upload failed (413 Payload Too Large)." : `Upload failed (${xhr.status})`;
      try {
        const parsed = JSON.parse(xhr.responseText) as { error?: string };
        if (parsed.error) message = parsed.error;
      } catch {
        // Response was not JSON.
      }
      reject(new Error(message));
    };
    xhr.onerror = () => reject(new Error("Upload network error"));
    xhr.send(body);
  });
}

async function uploadChunked(
  projectId: string,
  file: File,
  chunkSize: number,
  onProgress?: (progress: UploadProgress) => void,
): Promise<void> {
  const totalParts = Math.max(1, Math.ceil(file.size / chunkSize));
  const report = trackUpload(file.size, totalParts);
  onProgress?.(report(0, 0));
  for (let partNumber = 1; partNumber <= totalParts; partNumber++) {
    const start = (partNumber - 1) * chunkSize;
    const end = Math.min(start + chunkSize, file.size);
    const blob = file.slice(start, end);
    await sendBody(
      "PUT",
      `/api/admin/projects/${projectId}/upload-chunk?partNumber=${partNumber}`,
      blob,
      { "Content-Type": "application/octet-stream" },
      true,
      (loaded) => onProgress?.(report(start + loaded, partNumber)),
    );
    onProgress?.(report(end, partNumber));
  }
}

async function uploadDirect(
  upload: {
    method: string;
    url: string;
    headers: Record<string, string>;
    local: boolean;
  },
  file: File,
  onProgress?: (progress: UploadProgress) => void,
): Promise<void> {
  if (upload.local && file.size > 30 * 1024 * 1024) {
    throw new Error(
      "This deployment is using local storage. Large .3tz files need STORAGE_DRIVER=spaces.",
    );
  }
  const report = trackUpload(file.size, null);
  onProgress?.(report(0, null));
  await sendBody(upload.method, upload.url, file, upload.headers, upload.local, (loaded) =>
    onProgress?.(report(loaded, null)),
  );
  onProgress?.(report(file.size, null));
}

export async function uploadFile(
  projectId: string,
  upload: UploadPlan,
  file: File,
  onProgress?: (progress: UploadProgress) => void,
): Promise<void> {
  if (upload.mode === "chunked") {
    await uploadChunked(projectId, file, upload.chunkSize, onProgress);
    return;
  }
  await uploadDirect(
    {
      method: upload.method,
      url: upload.url,
      headers: upload.headers,
      local: upload.local,
    },
    file,
    onProgress,
  );
}
